// "Converting" — from found to repair order, in the REAL pipeline stages
// (Pending → Confirmed → In progress → Scheduled → Completed, Declined aside).
// The dollar-flow card maps every identified $ to where it sits (In motion ·
// Won · Lost — the CEO's vocabulary), the follow-up line calls out what's
// stuck, and the trend lines track close rate vs the benchmark and captured $.

import { Link } from "react-router"
import { Clock, ArrowUpRight, AlertTriangle } from "@untitledui/icons"
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine, ReferenceDot } from "recharts"
import { StatusBadge } from "@/components/estimates/status-badge"
import type { ApiEstimate } from "@/lib/api/types"
import { vehicleDisplayName } from "@/lib/api/types"
import { ChapterSection, ChartCard, LegendRow, BarTip, Highlight } from "../room-chrome"
import { CampaignResultsCard } from "../campaign-results-card"
import { AGING_HOURS, AXIS_TICK, CAT, FOAM, GRID_STROKE, INDUSTRY_CLOSE_RATE, MINT_PALE, RAMP, ageHours, fmt$, fmt$full, fmtAge, isStructural } from "../room-model"
import type { Headline, DollarFlow, FollowupFlags, HighlightAction, HighlightFinding, deriveCapture, deriveTrends } from "../derive"

type CaptureData = ReturnType<typeof deriveCapture>
type Trends = ReturnType<typeof deriveTrends>

export function ConvertingChapter({ d, captureData, flow, flags, trends, highlight, onHighlight, recoveryList, recoveryTotal, onPeek, channelLabel, rangeLabel }: {
  d: Headline
  captureData: CaptureData
  flow: DollarFlow
  flags: FollowupFlags
  trends: Trends
  highlight?: HighlightFinding
  onHighlight: (a: HighlightAction) => void
  recoveryList: ApiEstimate[]
  recoveryTotal: number
  onPeek: (e: ApiEstimate) => void
  channelLabel: string
  rangeLabel: string
}) {
  const structuralInQueue = recoveryList.some(isStructural)
  const funnelStages = [
    { label: "Pending", n: d.funnel.found, color: FOAM, outlined: true, text: "text-zinc-800" },
    { label: "Confirmed", n: d.funnel.confirmed, color: RAMP[0], text: "text-white" },
    { label: "In progress", n: d.funnel.inProgress, color: RAMP[1], text: "text-white" },
    { label: "Scheduled", n: d.funnel.scheduled, color: RAMP[2], text: "text-white" },
    { label: "Completed (RO)", n: d.funnel.completed, color: CAT.deep, text: "text-white" },
  ]
  // Advance rates between stages — the drop-off IS what a funnel is for.
  const advancePct = (from: number, to: number) => (from > 0 ? Math.round((to / from) * 100) : 0)

  const followups: { n: number; text: string; to: string }[] = [
    { n: flags.pendingStale, text: "pending past 24h — nobody's acted", to: "/estimates?status=Pending" },
    // Straight into the queue's "not called yet" bucket — the flag now names a
    // specific pile of people, so it should land on the screen that works them.
    { n: flags.confirmedStale, text: "confirmed past 48h — nobody has called", to: "/estimates?layout=followups&bucket=new" },
    { n: flags.missedAppointments, text: "scheduled date passed without completion", to: "/estimates?status=Scheduled" },
    { n: flags.noContact, text: "open with no customer phone or email — collect at write-up", to: "/estimates?status=Pending" },
    { n: flags.parked, text: "parked (deferred) — revisit", to: "/estimates" },
  ].filter(f => f.n > 0)

  return (
    <ChapterSection id="converting" eyebrow="Conversion" title="From found to repair order" audience="For leadership">
      {/* Pipeline dollar flow — where the money sits */}
      <ChartCard title="Estimate pipeline — where the money sits"
        sub={`Every identified dollar mapped to its stage · ${channelLabel} · ${rangeLabel}`}
        className="mb-3"
        info="Pending = nobody's acted yet · Confirmed = body shop confirmed, outreach not started · In progress = reaching out.">
        {highlight && <Highlight text={highlight.text} onClick={highlight.action ? () => onHighlight(highlight.action!) : undefined} />}
        <p className="text-xl font-bold tabular-nums text-zinc-900 mb-3">
          {fmt$full(flow.total)} <span className="text-xs font-normal text-[#8B897E]">total identified in period</span>
          {flow.medianToConfirm != null && (
            <span className="text-xs font-normal text-[#8B897E]"> · shop confirms in ~<strong className="text-zinc-700 font-semibold tabular-nums">{flow.medianToConfirm.toFixed(1)}d</strong> median</span>
          )}
        </p>
        <div className="flex h-8 rounded-lg overflow-hidden bg-[#F1F0EB]" role="img" aria-label="Proportional bar of pipeline value by stage">
          {flow.stages.filter(s => s.value > 0).map(s => (
            <div key={s.key} title={`${s.label}: ${fmt$full(s.value)}`} className="h-full min-w-0 relative transition-all duration-300" style={{ width: `${s.share}%`, background: s.color }}>
              {s.share >= 12 && <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-white truncate px-1">{fmt$(s.value)}</span>}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
          {flow.stages.map(s => (
            <Link key={s.key} to={`/estimates?status=${s.key}`}
              className="rounded-lg border border-[#ECEBE6] px-2.5 py-2 min-w-0 hover:border-[#D6D3C8] transition-colors">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold text-[#6E6C64] mb-1">
                <span className="size-2 rounded-[3px] shrink-0" style={{ background: s.color }} />{s.label}
              </p>
              <p className="text-base font-bold tabular-nums leading-none text-zinc-900">{s.count}</p>
              <p className={`text-[11px] font-bold tabular-nums mt-1 ${s.key === "Declined" ? "text-[#A32D2D]" : "text-[#0F6E56]"}`}>{s.count ? fmt$(s.value) : "—"}</p>
              <p className="text-[10px] text-[#8B897E] mt-0.5">{s.count ? `${Math.round(s.share)}% of pipeline` : "none in period"}</p>
            </Link>
          ))}
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <div className="flex-1 min-w-[150px] rounded-lg px-3 py-2 text-[11.5px] font-semibold flex items-center justify-between gap-2 bg-[#E8F8F2] text-[#085041]">
            <span>In motion (Pending → In progress)</span><b className="tabular-nums text-sm">{fmt$(flow.inMotion)}</b>
          </div>
          <div className="flex-1 min-w-[150px] rounded-lg px-3 py-2 text-[11.5px] font-semibold flex items-center justify-between gap-2" style={{ background: "#EAF3DE", color: "#27500A" }}>
            <span>Won (Scheduled + Completed)</span><b className="tabular-nums text-sm">{fmt$(flow.won)}</b>
          </div>
          <div className="flex-1 min-w-[150px] rounded-lg px-3 py-2 text-[11.5px] font-semibold flex items-center justify-between gap-2" style={{ background: "#FCEBEB", color: "#A32D2D" }}>
            <span>Lost (Declined)</span><b className="tabular-nums text-sm">{fmt$(flow.lost)}</b>
          </div>
        </div>
        {followups.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#ECEBE6] flex flex-wrap gap-x-5 gap-y-1.5">
            {followups.map(f => (
              <Link key={f.text} to={f.to} className="text-[12px] text-[#6E6C64] hover:text-zinc-800 transition-colors">
                <strong className="text-amber-700 tabular-nums">{f.n}</strong> {f.text}
              </Link>
            ))}
            {flags.upcomingAppointments > 0 && (
              <span className="text-[12px] text-[#6E6C64]"><strong className="text-[#0F6E56] tabular-nums">{flags.upcomingAppointments}</strong> scheduled in the next 7 days</span>
            )}
            {flags.comeback.count > 0 && (
              <span className="text-[12px] text-[#6E6C64]"><strong className="text-[#0F6E56] tabular-nums">{flags.comeback.count}</strong> deferred estimate{flags.comeback.count !== 1 ? "s" : ""} scheduled to return · <strong className="tabular-nums text-zinc-800">{fmt$(flags.comeback.value)}</strong></span>
            )}
          </div>
        )}
      </ChartCard>

      {/* Recovery queue — the follow-up to-do list (backlog: ignores the range) */}
      <div id="recovery-queue" className="animate-element rounded-2xl border border-amber-200/70 bg-amber-50/40 overflow-hidden mb-3 scroll-mt-[calc(var(--room-rail-h,72px)+16px)] print:break-inside-avoid">
        <div className="px-5 pt-4 pb-3 flex items-baseline justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
            <Clock className="size-4 text-amber-600" aria-hidden />
            Recover before it walks out
          </p>
          <p className="text-xs text-[#6E6C64]">
            <span className="font-semibold text-amber-700 tabular-nums">{fmt$(recoveryTotal)}</span> in {recoveryList.length} estimate{recoveryList.length !== 1 ? "s" : ""} older than {AGING_HOURS}h
            {structuralInQueue && <span className="ml-2 inline-flex items-center gap-1 text-red-600"><AlertTriangle className="size-3" aria-hidden />structural waiting</span>}
          </p>
        </div>
        {recoveryList.length === 0 ? (
          <p className="px-5 pb-4 text-xs text-[#8B897E]">Nothing aging in this view. Clean lane.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 border-t border-amber-100/80">
              {recoveryList.slice(0, 8).map(e => (
                <button key={e.id} onClick={() => onPeek(e)}
                  className="text-left px-5 py-2.5 border-b border-amber-100/60 md:odd:border-r hover:bg-white/70 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-medium text-zinc-900 truncate">{vehicleDisplayName(e.vehicle)}</p>
                    <p className="text-[13px] font-semibold tabular-nums text-zinc-900 shrink-0">{fmt$(e.avg ?? 0)}</p>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-[11px] text-[#8B897E] truncate">
                      {e.createdBy ? `${e.createdBy.firstName} ${e.createdBy.lastName[0]}.` : "Unassigned"} · {fmtAge(ageHours(e))}
                      {isStructural(e) && <span className="ml-1.5 text-[9px] font-bold text-red-600 uppercase tracking-wider">structural</span>}
                    </p>
                    <StatusBadge status={e.status} />
                  </div>
                </button>
              ))}
            </div>
            <Link to="/estimates?status=Pending" className="flex items-center justify-center gap-1 py-2.5 text-xs font-medium text-[#6E6C64] hover:text-zinc-800 transition-colors">
              {/* The grid caps at 8 — when it truncates, the footer says how
                  much is really waiting instead of a vague "all" (#56). */}
              {recoveryList.length > 8
                ? `View all ${recoveryList.length} aging estimates`
                : "All pending"}{" "}
              <ArrowUpRight className="size-3" aria-hidden />
            </Link>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Conversion funnel" sub={`Counts at or beyond each pipeline stage · ${channelLabel} · ${rangeLabel}`}>
          <div className="space-y-1 mt-2">
            {funnelStages.map((s, i) => (
              <div key={s.label}>
                {i > 0 && (
                  <p className="text-[11px] py-0.5 pl-2">
                    <span className="font-bold text-[#0F6E56] tabular-nums">↓ {advancePct(funnelStages[i - 1].n, s.n)}% advance</span>
                    {i === 3 && <span className="text-[#8B897E]"> · median {d.funnel.medToSchedule.toFixed(1)}d to schedule</span>}
                    {i === 4 && <span className="text-[#8B897E]"> · median {d.funnel.medToComplete.toFixed(1)}d to complete</span>}
                  </p>
                )}
                <div className="h-8 rounded-lg flex items-center px-3 justify-between transition-all duration-300"
                  style={{
                    width: `${Math.max((s.n / Math.max(d.funnel.found, 1)) * 100, 16)}%`,
                    background: s.color,
                    border: s.outlined ? `1px solid ${MINT_PALE}` : undefined,
                  }}>
                  <span className={`text-xs font-semibold whitespace-nowrap ${s.text}`}>{s.label}</span>
                  <span className={`text-xs font-bold tabular-nums pl-3 ${s.text}`}>{s.n}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#8B897E] mt-4">
            {d.funnel.declined > 0 && <><strong className="text-[#A32D2D] tabular-nums">{d.funnel.declined}</strong> declined · </>}
            {d.funnel.found - d.funnel.confirmed} not yet confirmed · {d.funnel.scheduled - d.funnel.completed} scheduled but not closed
          </p>
        </ChartCard>

        <ChartCard title="Identified vs. converted opportunity" sub={`Real collision found — ${channelLabel} — vs. routed to repair`}>
          {({ expanded }) => (
            <>
              <LegendRow items={[
                { label: `Identified ${fmt$(d.identified)}`, color: CAT.deep },
                { label: `Converted ${fmt$(d.converted)}`, color: CAT.brand },
              ]} />
              <div style={{ height: expanded ? 360 : 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={captureData}>
                    <CartesianGrid vertical={false} stroke={GRID_STROKE} />
                    <XAxis dataKey="name" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                    <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={44} tickFormatter={v => `$${Math.round(v / 1000)}k`} />
                    <Tooltip content={<BarTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={expanded ? 120 : 64}>
                      {captureData.map(c => <Cell key={c.name} fill={c.color} stroke={c.stroke} strokeWidth={c.stroke ? 1 : 0} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </ChartCard>

        {/* Trends over time */}
        <ChartCard title="Conversion rate over time" sub={`Close rate per day vs the ${INDUSTRY_CLOSE_RATE}% industry benchmark · ${rangeLabel}`}>
          {({ expanded }) => (
            <div style={{ height: expanded ? 360 : 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends.rows} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={GRID_STROKE} />
                  <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
                  <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={40} domain={[0, "auto"]} tickFormatter={v => `${v}%`} />
                  <Tooltip content={<BarTip />} cursor={{ stroke: "rgba(0,0,0,0.1)" }} />
                  <ReferenceLine y={INDUSTRY_CLOSE_RATE} stroke={MINT_PALE} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="closeRate" name="Close rate" stroke={CAT.deep} strokeWidth={2.5} dot={{ r: 2.5, fill: CAT.deep }} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Captured revenue over time"
          sub={`Completed-RO $ per day${trends.hasPrev ? " — dashed line is the prior period, day-aligned" : ""} · ${rangeLabel}`}>
          {({ expanded }) => (
            <div style={{ height: expanded ? 360 : 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends.rows} margin={{ top: 14, right: 12, left: -8, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={GRID_STROKE} />
                  <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
                  <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={44} tickFormatter={v => `$${Math.round(v / 1000)}k`} />
                  <Tooltip content={<BarTip />} cursor={{ stroke: "rgba(0,0,0,0.1)" }} />
                  {/* Ghost: the previous equal window, bucket-aligned — the comparison read */}
                  {trends.hasPrev && (
                    <Line type="monotone" dataKey="prevCaptured" name="Prior period" stroke="#C9C7BD" strokeWidth={1.5} strokeDasharray="5 4" dot={false} activeDot={false} />
                  )}
                  <Line type="monotone" dataKey="captured" name="Captured $" stroke={CAT.brand} strokeWidth={2.5} dot={{ r: 2.5, fill: CAT.brand }} activeDot={{ r: 4 }} />
                  {/* Best observed point — an earned superlative, labeled on the chart */}
                  {trends.best && (
                    <ReferenceDot x={trends.best.label} y={trends.best.captured} r={5} fill={CAT.deep} stroke="#fff" strokeWidth={2}
                      label={{ value: `best: ${fmt$(trends.best.captured)}`, position: "top", fontSize: 10, fill: "#0F6E56", fontWeight: 650 }} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>
      </div>
      <CampaignResultsCard />
    </ChapterSection>
  )
}
